/**
 * Congratulatory Messages for Wordle Game Completion
 * 
 * This file contains arrays of messages that are randomly selected based on
 * the number of attempts it took to guess the word, or if the word was not guessed.
 * 
 * Usage:
 * - guess1: Messages for getting the word in 1 attempt (perfect!)
 * - guess2: Messages for getting the word in 2 attempts (excellent!)
 * - guess3: Messages for getting the word in 3 attempts (great!)
 * - guess4: Messages for getting the word in 4 attempts (good!)
 * - guess5: Messages for getting the word in 5 attempts (close call!)
 * - guess6: Messages for getting the word in 6 attempts (just made it!)
 * - failed: Messages for when the word could not be guessed
 */

export const congratulationsMessages = {
    // Perfect score - 1 attempt
    guess1: [
        "🎯 Ongelooflik! Perfekte telling!",
        "🌟 Briljant! Eerste keer raak!",
        "⚡ Fenomenaal! Jy's 'n woordmeester!",
        "🏆 Uitstekend! Perfekte spel!",
        "🎊 Wonderlik! Eerste poging sukses!",
        "💎 Uitsonderlik! Jy's 'n legende!",
        "🚀 Onoortrefflik! Perfekte raai!",
        "✨ Magies! Een poging, een woord!"
    ],

    // Excellent - 2 attempts
    guess2: [
        "🎉 Uitstekend! Baie goed gedoen!",
        "🌟 Briljant! Twee pogings is fantasties!",
        "🏅 Wonderlik! Jy's baie goed!",
        "⭐ Uitnemend! Perfekte spel!",
        "🎯 Fantasties! Vinnige denke!",
        "💫 Indrukwekkend! Goeie werk!",
        "🔥 Uitstekend! Jy's skerp!",
        "🎊 Bravo! Twee pogings is perfek!"
    ],

    // Great - 3 attempts
    guess3: [
        "👏 Goed gedoen! Drie pogings is goed!",
        "🌈 Lekker! Jy's op die regte spoor!",
        "⚡ Mooi! Goeie woordkennis!",
        "🎯 Fantasties! Halfpad sukses!",
        "🌟 Wonderlik! Jy verstaan die spel!",
        "💪 Sterk! Drie pogings is solied!",
        "🎉 Uitstekend! Goeie strategie!",
        "✨ Mooi werk! Jy's op dreef!"
    ],

    // Good - 4 attempts
    guess4: [
        "👍 Goed! Vier pogings is nog steeds goed!",
        "🌻 Lekker! Jy kom nader!",
        "⭐ Mooi! Goeie volharding!",
        "🎯 Goed gedoen! Jy's besig om te leer!",
        "💚 Wonderlik! Vier pogings werk!",
        "🌟 Fantasties! Jy hou aan probeer!",
        "🎊 Goed! Jy's op die regte pad!",
        "✨ Mooi! Volharding betaal!"
    ],

    // Close call - 5 attempts
    guess5: [
        "😅 Naby! Vyf pogings is nog steeds 'n wen!",
        "🤞 Spannend! Jy het dit reggekry!",
        "💪 Volharding! Vyf pogings werk!",
        "🌟 Goed! Jy het nie opgegee nie!",
        "🎯 Mooi! Net betyds reggekry!",
        "⚡ Lekker! Laaste oomblik sukses!",
        "🎉 Fantasties! Jy het volgehou!",
        "✨ Wonderlik! Vyf pogings is genoeg!"
    ],

    // Just made it - 6 attempts
    guess6: [
        "😰 Net betyds! Ses pogings presies reg!",
        "🤯 Spannend! Laaste kans sukses!",
        "💪 Volharding betaal! Jy het dit reggekry!",
        "🎯 Wow! Net op tyd!",
        "⚡ Indrukwekkend! Laaste poging sukses!",
        "🌟 Fantasties! Jy het nie opgegee nie!",
        "🎊 Wonderlik! Ses pogings is genoeg!",
        "✨ Bravo! Volharding wen!"
    ],

    // Failed to guess the word
    failed: [
        "😔 Jammer! Die woord was te moeilik vandag.",
        "🤔 Beter geluk môre! Probeer weer!",
        "💪 Moenie opgee nie! Môre is 'n nuwe dag!",
        "🌟 Goed probeer! Jy sal beter doen môre!",
        "⚡ Dis oukei! Elke dag is 'n nuwe kans!",
        "🎯 Probeer weer môre! Jy kan dit doen!",
        "🌈 Moenie worry nie! Volgende keer beter!",
        "✨ Goed probeer! Kom ons probeer weer môre!",
        "🤞 Dis deel van die spel! Probeer weer!",
        "💚 Jy het goed probeer! Môre is 'n nuwe woord!"
    ]
}

/**
 * Get a random congratulatory message based on the number of attempts
 * @param attempts - Number of attempts taken (1-6) or 0 for failed
 * @returns Random message string
 */
export function getRandomCongratulationMessage(attempts: number): string {
    let messageArray: string[]

    switch (attempts) {
        case 1:
            messageArray = congratulationsMessages.guess1
            break
        case 2:
            messageArray = congratulationsMessages.guess2
            break
        case 3:
            messageArray = congratulationsMessages.guess3
            break
        case 4:
            messageArray = congratulationsMessages.guess4
            break
        case 5:
            messageArray = congratulationsMessages.guess5
            break
        case 6:
            messageArray = congratulationsMessages.guess6
            break
        default:
            messageArray = congratulationsMessages.failed
            break
    }

    const randomIndex = Math.floor(Math.random() * messageArray.length)
    return messageArray[randomIndex]
}